import React, { useCallback, useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import { Box, CircularProgress } from "@mui/material";
import { isValidInput } from "../../utils/validations";
import useTranslation from "../../hooks/traslation";
import MainContainer from "../../layout/MainContainer";
import PayoutList from "../../features/managePayout/PayoutList";
import { useLazyGetPayOutQuery, useUpdatePayoutAmountMutation } from "../../services/payout";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";

function ManagePayout() {
  const [getPayOut] = useLazyGetPayOutQuery();
  const [updatePayoutAmount] = useUpdatePayoutAmountMutation();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const translation = useTranslation();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedPayoutId, setSelectedPayoutId] = useState<string>("");
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const limit = 10;

  const handleOpenConfirmDialog = useCallback((id: string) => {
    setSelectedPayoutId(id);
    setOpenConfirmModal(true);
  }, []);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const getAllPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPayOut({
        page,
        limit,
        search: debouncedSearchTerm.trim(),
      }).unwrap();

      if (response?.statusCode === 200) {
        setLoading(false);
        setRows(response?.data[0]?.list || []);
        setCount(response?.data[0]?.totalCount);
      } else {
        setLoading(false);
        setRows([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  }, [page, debouncedSearchTerm, getPayOut]);

  const handleConfirmPay = async () => {
    if (!selectedPayoutId) return;
    setIsConfirmLoading(true);
    try {
      const response = await updatePayoutAmount({ id: selectedPayoutId }).unwrap();
      if (response?.statusCode === 200) {
        showToast("Payout completed successfully");
        setOpenConfirmModal(false);
        setSelectedPayoutId("");
        getAllPayouts();
      } else {
        showError(response?.message || "Failed to process payout");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to process payout");
    } finally {
      setIsConfirmLoading(false);
    }
  };

  useEffect(() => {
    getAllPayouts();
  }, [getAllPayouts]);
  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Payout</h1>
        <div className="cards">
          <Box className="search_box" style={{ marginBottom: "20px" }}>
            <SearchBar
              placeholder={translation.user.SearchHere}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
          </Box>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <PayoutList
                rows={rows}
                isLoading={loading}
                count={count}
                page={page}
                onPageChange={handlePageChange}
                onRefresh={getAllPayouts}
                handleOpenConfirmDialog={handleOpenConfirmDialog}
              />
              {rows.length !== 0 && (
                <div className="table_footer">
                  <div className="pagination_info">
                    <span className="total_records">Showing {rows.length} of {count} records</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ConfirmationModal
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        text="Are you sure you want to mark this payout as paid?"
        handleSubmit={handleConfirmPay}
        isLoading={isConfirmLoading}
      />
    </MainContainer>
  );
}

export default ManagePayout;
